// Meta Pixel event tracking helpers
// Pixel ID 1972373679937156 is initialized in index.html
// PageView fires automatically on initial load.

declare global {
  interface Window {
    fbq?: (
      command: 'track' | 'trackCustom' | 'init',
      eventNameOrId: string,
      params?: Record<string, any>,
      options?: { eventID?: string },
    ) => void
  }
}

/**
 * Track a standard Meta Pixel event.
 * https://developers.facebook.com/docs/meta-pixel/reference#standard-events
 */
export function trackEvent(
  eventName:
    | 'PageView'
    | 'Lead'
    | 'CompleteRegistration'
    | 'ViewContent'
    | 'InitiateCheckout'
    | 'AddToCart'
    | 'Purchase'
    | 'Contact'
    | 'SubmitApplication'
    | 'Search',
  params: Record<string, any> = {},
) {
  if (typeof window === 'undefined' || !window.fbq) return
  try {
    window.fbq('track', eventName, params)
    // Mirror to console in dev for verification
    if (typeof console !== 'undefined') {
      // eslint-disable-next-line no-console
      console.debug('[fbq] track', eventName, params)
    }
  } catch (err) {
    // Fail silently — pixel must never block UX
  }
}

/**
 * Track a custom Meta Pixel event (use for granular labels per affiliate/section).
 */
export function trackCustom(
  eventName: string,
  params: Record<string, any> = {},
) {
  if (typeof window === 'undefined' || !window.fbq) return
  try {
    window.fbq('trackCustom', eventName, params)
    if (typeof console !== 'undefined') {
      // eslint-disable-next-line no-console
      console.debug('[fbq] trackCustom', eventName, params)
    }
  } catch (err) {}
}

/**
 * Track an affiliate outbound click — fires both a standard InitiateCheckout
 * (so Ads Manager treats it as a high-intent action) AND a custom event
 * tagged with the specific product slug for granular reporting.
 */
export function trackAffiliateClick(productSlug: string, productName?: string) {
  trackEvent('InitiateCheckout', {
    content_name: productName || productSlug,
    content_ids: [productSlug],
    content_category: 'affiliate_outbound',
  })
  trackCustom('AffiliateClick', {
    product: productSlug,
    product_name: productName || productSlug,
  })
}

/**
 * Track a lead capture event (form submission).
 * Fires both Lead and CompleteRegistration for maximum Ads Manager flexibility.
 */
export function trackLead(source: string, extra: Record<string, any> = {}) {
  const params = {
    content_name: source,
    content_category: 'lead_capture',
    ...extra,
  }
  trackEvent('Lead', params)
  trackEvent('CompleteRegistration', params)
}

/**
 * Track an intent signal — user opened a CTA / scrolled to a key section.
 */
export function trackIntent(label: string, extra: Record<string, any> = {}) {
  trackEvent('ViewContent', {
    content_name: label,
    content_category: 'intent_signal',
    ...extra,
  })
}
